import { useBudget } from "../context/BudgetContext";

export default function Runway() {
  const { transactions, budgetLoading, runwaySettings, updateRunwaySettings } = useBudget();
  const {
    cashBalance = "", monthlyBurnOverride = "", mrr = "",
    monthsToClose = "6", targetPostRaiseRunway = "18", momGrowthRate = "",
    mrrHistory = {},
  } = runwaySettings;

  const set = (key) => (e) =>
    updateRunwaySettings({ [key]: e.target.value });

  const setMrr = (e) => {
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    updateRunwaySettings({
      mrr: e.target.value,
      mrrHistory: { ...mrrHistory, [monthKey]: e.target.value },
    });
  };

  if (budgetLoading) return <div className="page-loading" />;

  const now = new Date();
  const thisMonth = transactions.filter(tx => { const d = new Date(tx.date); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); });
  const txBurn = thisMonth.reduce((s, tx) => s + Number(tx.amount), 0);
  const burn = Number(monthlyBurnOverride) || txBurn || 0;
  const mrrNum = Number(mrr) || 0;
  const netBurn = Math.max(burn - mrrNum, 0);
  const cash = Number(cashBalance) || 0;
  const runwayMonths = netBurn > 0 && cash > 0 ? Math.floor(cash / netBurn) : null;
  const runwayDate = runwayMonths != null ? new Date(now.getFullYear(), now.getMonth() + runwayMonths, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : null;
  const runwayColor = runwayMonths == null ? "#333" : runwayMonths <= 3 ? "var(--red)" : runwayMonths <= 6 ? "var(--amber)" : "var(--green)";
  const runwayHover = runwayMonths == null ? "" : runwayMonths <= 3 ? "hover-red" : runwayMonths <= 6 ? "hover-amber" : "hover-green";
  const runwayStatus = runwayMonths == null ? "—" : runwayMonths <= 3 ? "critical" : runwayMonths <= 6 ? "watch closely" : "healthy";

  const mtcNum = Math.max(Number(monthsToClose) || 6, 1);
  const tprNum = Math.max(Number(targetPostRaiseRunway) || 18, 1);
  const raiseByMonths = runwayMonths != null ? runwayMonths - mtcNum : null;
  const raiseByDate = raiseByMonths != null
    ? new Date(now.getFullYear(), now.getMonth() + raiseByMonths, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : null;
  const raisePastDue = raiseByMonths != null && raiseByMonths <= 0;
  const raiseColor = raiseByMonths == null ? "#333" : raiseByMonths <= 0 ? "var(--red)" : raiseByMonths <= 2 ? "var(--amber)" : "var(--green)";
  const raiseHover = raiseByMonths == null ? "" : raiseByMonths <= 0 ? "hover-red" : raiseByMonths <= 2 ? "hover-amber" : "hover-green";
  const raiseUrgency = raiseByMonths == null ? "—" : raiseByMonths <= 0 ? "start now" : raiseByMonths <= 2 ? "urgent" : "on track";
  const targetRaise = netBurn > 0 ? netBurn * tprNum : null;

  // Burn Multiple — auto from mrrHistory
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthKey = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, "0")}`;
  const lastMrrNum = Number(mrrHistory[lastMonthKey]) || 0;
  const hasLastMonthMrr = !!mrrHistory[lastMonthKey];
  const netNewMrr = mrrNum - lastMrrNum;
  const netNewArr = netNewMrr * 12;
  const burnMultiple = hasLastMonthMrr && netBurn > 0 && netNewArr > 0 ? netBurn / netNewArr : null;
  const smartBudget = cash > 0 ? Math.floor(cash / tprNum) + mrrNum : 0;
  const bmLabel = burnMultiple == null ? null : burnMultiple < 1 ? "excellent" : burnMultiple < 1.5 ? "good" : burnMultiple < 2 ? "fair" : "poor";
  const bmColor = burnMultiple == null ? "#333" : burnMultiple < 1.5 ? "var(--green)" : burnMultiple < 2 ? "var(--amber)" : "var(--red)";
  const bmHover = burnMultiple == null ? "" : burnMultiple < 1.5 ? "hover-green" : burnMultiple < 2 ? "hover-amber" : "hover-red";

  // Default Alive simulation
  const momGrowth = Number(momGrowthRate) / 100;
  let defaultAlive = null;
  let defaultAliveMonth = null;
  if (cash > 0 && burn > 0 && momGrowthRate !== "") {
    if (mrrNum >= burn) {
      defaultAlive = true;
      defaultAliveMonth = 0;
    } else {
      let simCash = cash;
      let simMrr = mrrNum;
      for (let i = 1; i <= 72; i++) {
        simMrr = simMrr * (1 + momGrowth);
        simCash -= Math.max(burn - simMrr, 0);
        if (simMrr >= burn) { defaultAlive = true; defaultAliveMonth = i; break; }
        if (simCash <= 0)   { defaultAlive = false; defaultAliveMonth = i; break; }
      }
      if (defaultAlive === null) { defaultAlive = false; defaultAliveMonth = 72; }
    }
  }
  const defaultAliveDate = defaultAliveMonth != null
    ? new Date(now.getFullYear(), now.getMonth() + defaultAliveMonth, 1)
        .toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : null;

  const monthlySpend = Array.from({ length: 3 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (2 - i), 1);
    const spent = transactions.filter(tx => { const t = new Date(tx.date); return t.getMonth() === d.getMonth() && t.getFullYear() === d.getFullYear(); }).reduce((s, tx) => s + Number(tx.amount), 0);
    return { label: d.toLocaleDateString("en-US", { month: "short" }).toLowerCase(), spent };
  });
  const maxSpend = Math.max(...monthlySpend.map(m => m.spent), 1);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Runway</div>
          <div className="page-subtitle">$ burn rate & cash forecast</div>
        </div>
      </div>

      {/* Inputs */}
      <div className="card hover-green" style={{ marginBottom: 8 }}>
        <div className="label" style={{ marginBottom: 10 }}>YOUR_NUMBERS</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
          {[
            { label: "CASH_BALANCE ($)", val: cashBalance, onChange: set("cashBalance"), ph: "e.g. 500000" },
            { label: "MRR ($)", val: mrr, onChange: setMrr, ph: "e.g. 12000" },
            { label: `BURN_OVERRIDE ($) — auto: $${txBurn.toLocaleString()}`, val: monthlyBurnOverride, onChange: set("monthlyBurnOverride"), ph: "override" },
            { label: "MOM_GROWTH_RATE (%)", val: momGrowthRate, onChange: set("momGrowthRate"), ph: "e.g. 8" },
            { label: "MONTHS_TO_CLOSE (raise)", val: monthsToClose, onChange: set("monthsToClose"), ph: "e.g. 6" },
            { label: "TARGET_POST_RAISE_RUNWAY (mo)", val: targetPostRaiseRunway, onChange: set("targetPostRaiseRunway"), ph: "e.g. 18" },
          ].map(({ label, val, onChange, ph }) => (
            <div key={label}>
              <div className="label" style={{ marginBottom: 4 }}>{label}</div>
              <input type="number" placeholder={ph} value={val} onChange={onChange} />
            </div>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="stat-grid stat-grid-3" style={{ marginBottom: 8 }}>
        {[
          { label: "MONTHLY_BURN", value: burn > 0 ? `$${burn.toLocaleString()}` : "—", hover: "hover-amber" },
          { label: "NET_BURN", value: netBurn > 0 ? `$${netBurn.toLocaleString()}` : "—", hover: "hover-red" },
          { label: "RUNWAY", value: runwayMonths != null ? `${runwayMonths}mo` : "—", hover: runwayHover, color: runwayColor },
        ].map(({ label, value, hover, color }) => (
          <div key={label} className={`card ${hover}`}>
            <div className="label">{label}</div>
            <div className="value" style={color ? { color } : {}}>{value}</div>
          </div>
        ))}
      </div>

      {/* Smart Budget */}
      {smartBudget > 0 && (
        <div className="card hover-green" style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div className="label">SMART_BUDGET</div>
            <div className="mono" style={{ fontSize: 10, color: "#555", marginTop: 3 }}>
              max spend to maintain {tprNum}mo runway
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="mono" style={{ fontSize: 20, color: smartBudget >= burn ? "var(--green)" : "var(--red)" }}>
              ${smartBudget.toLocaleString()}
            </div>
            {burn > 0 && (
              <div className="mono" style={{ fontSize: 9, color: "#444", marginTop: 2 }}>
                {smartBudget >= burn ? `$${(smartBudget - burn).toLocaleString()} headroom` : `$${(burn - smartBudget).toLocaleString()} over budget`}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Burn Multiple */}
      <div className={`card ${bmHover}`} style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div className="label">BURN_MULTIPLE</div>
          {bmLabel && (
            <span className="badge" style={{ background: "transparent", border: `1px solid ${bmColor}44`, color: bmColor, fontSize: 10 }}>
              {bmLabel}
            </span>
          )}
        </div>
        {burnMultiple !== null ? (
          <>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 10 }}>
              <span className="mono" style={{ fontSize: 32, fontWeight: 500, color: bmColor, lineHeight: 1 }}>
                {burnMultiple.toFixed(2)}x
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span className="mono" style={{ fontSize: 9, color: "#444" }}>net burn / net new ARR</span>
                <span className="mono" style={{ fontSize: 9, color: "#444" }}>${netBurn.toLocaleString()} / ${netNewArr.toLocaleString()}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span className="mono" style={{ fontSize: 9, color: "#444" }}>net new MRR vs {lastMonthDate.toLocaleDateString("en-US", { month: "short" }).toLowerCase()}</span>
                <span className="mono" style={{ fontSize: 9, color: netNewMrr >= 0 ? "var(--green)" : "var(--red)" }}>
                  {netNewMrr >= 0 ? "+" : ""}${netNewMrr.toLocaleString()}
                </span>
              </div>
              <div style={{ paddingTop: 8, borderTop: "1px solid #111", display: "flex", gap: 12 }}>
                {[["<1x", "excellent", "var(--green)"], ["1–1.5x", "good", "var(--green)"], ["1.5–2x", "fair", "var(--amber)"], [">2x", "poor", "var(--red)"]].map(([range, lbl, col]) => (
                  <div key={range} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span className="mono" style={{ fontSize: 8, color: col }}>{range}</span>
                    <span className="mono" style={{ fontSize: 8, color: "#444" }}>{lbl}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <span className="mono" style={{ fontSize: 10, color: "#444" }}>
            {!hasLastMonthMrr && mrrNum > 0
              ? "auto-computes next month once mrr history builds"
              : hasLastMonthMrr && netNewMrr <= 0
              ? "mrr not growing — burn multiple undefined"
              : "enter mrr and burn to compute"}
          </span>
        )}
      </div>

      {/* Runway date */}
      {runwayDate && (
        <div className={`card ${runwayHover}`} style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div className="label">CASH_OUT_DATE</div>
            <div className="mono" style={{ fontSize: 14, color: "#888", marginTop: 2 }}>{runwayDate.toLowerCase()}</div>
          </div>
          <span className="badge" style={{ background: "transparent", border: `1px solid ${runwayColor}44`, color: runwayColor, fontSize: 10 }}>
            {runwayStatus}
          </span>
        </div>
      )}

      {/* Default Alive */}
      {defaultAlive !== null && (
        <div className={`card ${defaultAlive ? "hover-green" : "hover-red"}`} style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div className="label">DEFAULT_ALIVE</div>
            <span className="badge" style={{
              background: "transparent",
              border: `1px solid ${defaultAlive ? "var(--green)" : "var(--red)"}44`,
              color: defaultAlive ? "var(--green)" : "var(--red)",
              fontSize: 10,
            }}>
              {defaultAlive ? "alive" : "dead"}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
            <span className="mono" style={{
              fontSize: 28, fontWeight: 500,
              color: defaultAlive ? "var(--green)" : "var(--red)",
              lineHeight: 1,
            }}>
              {defaultAlive ? "DEFAULT ALIVE" : "DEFAULT DEAD"}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="mono" style={{ fontSize: 10, color: "#666" }}>
                {defaultAlive ? "reaches profitability" : "cash out"}
              </span>
              <span className="mono" style={{ fontSize: 11, color: defaultAlive ? "var(--green)" : "var(--red)" }}>
                {defaultAliveMonth === 0 ? "already profitable" : defaultAliveDate?.toLowerCase()}
              </span>
            </div>
            <div style={{ paddingTop: 8, borderTop: "1px solid #111" }}>
              <span className="mono" style={{ fontSize: 9, color: "#444" }}>
                assuming {momGrowthRate}% mom growth · {defaultAliveMonth === 0 ? "mrr already covers burn" : `${defaultAliveMonth}mo to crossover`}
              </span>
            </div>
          </div>
        </div>
      )}
      {momGrowthRate === "" && cash > 0 && burn > 0 && (
        <div className="card" style={{ marginBottom: 8 }}>
          <div className="label" style={{ marginBottom: 6 }}>DEFAULT_ALIVE</div>
          <span className="mono" style={{ fontSize: 10, color: "#444" }}>enter mom growth rate above to compute</span>
        </div>
      )}

      {/* Raise timeline */}
      {runwayMonths != null && (
        <div className={`card ${raiseHover}`} style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div className="label">RAISE_OR_DEFAULT</div>
            <span className="badge" style={{ background: "transparent", border: `1px solid ${raiseColor}44`, color: raiseColor, fontSize: 10 }}>
              {raiseUrgency}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span className="mono" style={{ fontSize: 10, color: "#666" }}>start raise by</span>
              <span className="mono" style={{ fontSize: 13, color: raiseColor }}>
                {raisePastDue ? "already past due" : raiseByDate?.toLowerCase()}
              </span>
            </div>
            {targetRaise != null && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span className="mono" style={{ fontSize: 10, color: "#666" }}>target raise ({tprNum}mo runway)</span>
                <span className="mono" style={{ fontSize: 13, color: "#aaa" }}>${targetRaise.toLocaleString()}</span>
              </div>
            )}
            <div style={{ marginTop: 2, paddingTop: 8, borderTop: "1px solid #111" }}>
              <span className="mono" style={{ fontSize: 9, color: "#444", lineHeight: 1.6 }}>
                based on {runwayMonths}mo runway · {mtcNum}mo to close · closing by {new Date(now.getFullYear(), now.getMonth() + (raiseByMonths ?? 0) + mtcNum, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" }).toLowerCase()}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Trend */}
      <div className="card hover-blue">
        <div className="label" style={{ marginBottom: 12 }}>SPEND_TREND — last 3 months</div>
        {transactions.length === 0 ? (
          <span className="mono" style={{ fontSize: 11, color: "#555" }}>no transactions yet</span>
        ) : (
          <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 80 }}>
            {monthlySpend.map(({ label, spent }) => (
              <div key={label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                <span className="mono" style={{ fontSize: 9, color: "#666" }}>${spent.toLocaleString()}</span>
                <div style={{ width: "100%", background: "#0d0d0d", borderRadius: 3, overflow: "hidden", height: 60, display: "flex", alignItems: "flex-end" }}>
                  <div style={{ width: "100%", height: `${(spent / maxSpend) * 100}%`, background: "var(--green)", borderRadius: "2px 2px 0 0", transition: "height 0.5s ease" }} />
                </div>
                <span className="mono" style={{ fontSize: 9, color: "#666" }}>{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
